import * as S from "./styles";
import * as Dialog from "@radix-ui/react-dialog";
import { Input } from "../../../../../../components/New/Input";
import { SwitchInput } from "../../../../../../components/Switch/SwitchInput";
import { IPrinter, IStore } from "../../../../../../@types/Stores";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { PrinterSchema, TPrinterSchema } from "../../RegisterStore/schema";
import { Button } from "../../../../../../components/New/Button";
import { wrapperRequests } from "../../../../../../services/api";
import { routesURL } from "../../../../../../services/routesUrl";
import { useState, useEffect } from "react";
import { useToast } from "../../../../../../hooks/useToast";
import { SelectInput } from "../../../../../../components/SelectInput";
import { useMediaQuery } from "usehooks-ts";
import { useTheme } from "styled-components";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";

interface PrintersModalContentProps {
  printer?: IPrinter;
  storeData?: IStore;
  onSuccess?: () => void;
}

export function PrintersModalContent({
  printer = {} as IPrinter,
  storeData,
  onSuccess,
}: PrintersModalContentProps) {
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const matches = useMediaQuery(`(max-width: ${theme.responsiveDesign.sm})`);
  const { toastify } = useToast();
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
    reset,
    setValue,
    watch,
  } = useForm<TPrinterSchema>({
    resolver: zodResolver(PrinterSchema),
    defaultValues: {
      id: printer?.id,
      active: printer?.active ?? true,
      activeForProductPrice: printer?.activeForProductPrice || false,
      activeForWarehouseLabel: printer?.activeForWarehouseLabel || false,
      activeForTransferCompleteLabel:
        printer?.activeForTransferCompleteLabel || false,
      activeForOrderItem: printer?.activeForOrderItem || false,
      activeForManualTransferCompleteLabel:
        printer?.activeForManualTransferCompleteLabel || false,
      size: printer.size || "Small - 2.25 x 1.25",
    },
  });

  const activeForProductPrice = watch("activeForProductPrice");
  const activeForWarehouseLabel = watch("activeForWarehouseLabel");
  const activeForTransferCompleteLabel = watch(
    "activeForTransferCompleteLabel",
  );
  const activeForOrderItem = watch("activeForOrderItem");
  const activeForManualTransferCompleteLabel = watch(
    "activeForManualTransferCompleteLabel",
  );
  const { active, size } = watch();

  const hasLargeSwitchActive =
    activeForOrderItem ||
    activeForWarehouseLabel ||
    activeForTransferCompleteLabel ||
    activeForManualTransferCompleteLabel;

  useEffect(() => {
    if (activeForProductPrice) {
      setValue("size", "Small - 2.25 x 1.25");
    } else if (hasLargeSwitchActive) {
      setValue("size", "Large - 2.25 x 3");
    } else {
      setValue("size", "Small - 2.25 x 1.25");
    }
  }, [activeForProductPrice, hasLargeSwitchActive, setValue]);

  function setMultipleValuesFromObject(values: Partial<TPrinterSchema>) {
    Object.entries(values).forEach(([key, value]) =>
      setValue(key as keyof TPrinterSchema, value),
    );
  }

  const handleSwitchChange = (fieldName: keyof TPrinterSchema) => {
    const currentValue = watch(fieldName);
    const newValue = !currentValue;
    setValue(fieldName, newValue);

    if (newValue) {
      if (fieldName === "activeForProductPrice") {
        setMultipleValuesFromObject({
          activeForOrderItem: false,
          activeForWarehouseLabel: false,
          activeForTransferCompleteLabel: false,
          activeForManualTransferCompleteLabel: false,
        });
      } else {
        setMultipleValuesFromObject({
          activeForProductPrice: false,
        });
      }
    }
  };

  async function handleCreatePrinter(data: TPrinterSchema) {
    setLoading(true);
    const initialPrinters = storeData?.printers || [];

    const removeUpdatedPrinter = initialPrinters.filter(
      (printer) => printer.id !== data.id,
    );

    if (storeData?.tenantName && storeData.code) {
      try {
        await wrapperRequests(
          routesURL.settings.stores.editStore(
            storeData?.tenantName,
            storeData.code,
          ),
          "PUT",
          {
            data: {
              printers: [...removeUpdatedPrinter, data],
            },
          },
        );

        reset();

        if (onSuccess) {
          onSuccess();
        }



        toastify("success", t("success"), "top-center");
      } catch (error) {
        if (error instanceof AxiosError) {
          toastify(
            "error",
            error.response?.data.message ||
              t("error"),
            "top-center",
          );
        } else {
          toastify(
            "error",
            t("error"),
            "top-center",
          );
        }
        throw Error(String(error));
      } finally {
        setLoading(false);
      }
    }
  }

  const handleCancel = () => {
    clearErrors();
    const closeElement = document
      .querySelector("[data-radix-focus-guard]")
      ?.parentElement?.querySelector("[data-state='open']");
    if (closeElement) {
      (closeElement as HTMLElement).click();
    }
  };

  const renderSaveButton = () => (
    <Button
      type="button"
      title={t("save")}
      isLoading={loading}
      disabled={loading}
      onClick={handleSubmit(handleCreatePrinter)}
      width={matches ? "100%" : undefined}
    />
  );

  const renderCancelButton = () => (
    <Button
      title={t("cancel")}
      variant="ghost"
      onClick={handleCancel}
      disabled={loading}
      width={matches ? "100%" : undefined}
    />
  );

  return (
    <S.ModalFormContent>
      <S.ModalAlign style={{ alignItems: "center" }}>
        <S.GridItemStart>
          <SwitchInput
            title={t("active")}
            label={active ? t("on") : t("off")}
            checked={!!active}
            onChange={() => setValue("active", !active)}
          />
        </S.GridItemStart>
        <S.GridSpacer />

        <Input
          title={t("serialNumber")}
          defaultValue={printer?.serialNumber}
          {...register("serialNumber")}
          errorMessage={errors.serialNumber?.message}
          hasAsterisk
        />

        <Input
          title={t("tag")}
          defaultValue={printer?.tag}
          {...register("tag")}
          errorMessage={errors.tag?.message}
          hasAsterisk
        />

        <SelectInput
          title={t("size")}
          variant="resized"
          value={size}
          disabled={true}
          options={[
            {
              id: "1",
              value: "Small - 2.25 x 1.25",
              label: t("printerSizeSmall"),
            },
            {
              id: "2",
              value: "Large - 2.25 x 3",
              label: t("printerSizeLarge"),
            },
            {
              id: "3",
              value: "ExtraLarge - 4 x 6",
              label: t("printerSizeExtraLarge"),
            },
          ]}
          {...register("size")}
        />

        <SwitchInput
          title={t("productPrice")}
          label={activeForProductPrice ? "On" : "Off"}
          checked={!!activeForProductPrice}
          disabled={hasLargeSwitchActive}
          onChange={() => handleSwitchChange("activeForProductPrice")}
        />

        <SwitchInput
          title={t("orderItem")}
          label={activeForOrderItem ? "On" : "Off"}
          checked={!!activeForOrderItem}
          disabled={activeForProductPrice}
          {...register("activeForOrderItem")}
          onChange={() => handleSwitchChange("activeForOrderItem")}
        />

        <SwitchInput
          title={t("warehouseLabel")}
          label={activeForWarehouseLabel ? "On" : "Off"}
          checked={!!activeForWarehouseLabel}
          disabled={activeForProductPrice}
          onChange={() => handleSwitchChange("activeForWarehouseLabel")}
        />

        <SwitchInput
          title={t("transferLabel")}
          label={activeForTransferCompleteLabel ? "On" : "Off"}
          checked={!!activeForTransferCompleteLabel}
          disabled={activeForProductPrice}
          onChange={() => handleSwitchChange("activeForTransferCompleteLabel")}
        />

        <SwitchInput
          title={t("manualTransferLabel")}
          label={activeForManualTransferCompleteLabel ? "On" : "Off"}
          checked={!!activeForManualTransferCompleteLabel}
          disabled={activeForProductPrice}
          onChange={() =>
            handleSwitchChange("activeForManualTransferCompleteLabel")
          }
        />
      </S.ModalAlign>

      <S.ButtonContainer>
        {matches ? (
          <>
            {renderSaveButton()}
            {renderCancelButton()}
          </>
        ) : (
          <>
            <Dialog.Close asChild>{renderCancelButton()}</Dialog.Close>
            {renderSaveButton()}
          </>
        )}
      </S.ButtonContainer>
    </S.ModalFormContent>
  );
}
